import { useCallback, useMemo } from "react";
import { QueryConfig, QueryKey, useQuery } from "react-query";

import { getResourceUuid, ProfessionalSituationBrief } from "@sol/sdk";
import { useApi } from "@sol/sdk/SDKProvider";
import { unwrapAPIResult } from "@sol/sdk/utils/api";

import { IBrief } from "../../briefs/IBrief";
import { briefQuery } from "../../briefs/queries/briefQuery";

export type IProfessionalSituationBriefsQueryParameters = {
    briefs?: ProfessionalSituationBrief[];
};

export function useProfessionalSituationBriefsQuery(
    { briefs }: IProfessionalSituationBriefsQueryParameters,
    config?: QueryConfig<IBrief[]>,
) {
    const api = useApi();
    const briefIds = useMemo(() => briefs?.map(brief => getResourceUuid(brief)) ?? [], [briefs]);

    const queryKey: QueryKey = useMemo(() => ["professional-situation-briefs", briefIds], [briefIds]);

    const queryFn = useCallback(async (): Promise<IBrief[]> => {
        if (briefIds.length === 0) {
            return [];
        }

        try {
            const allBriefs = await Promise.all(
                briefIds.map(async briefId => {
                    try {
                        const config = briefQuery({ brief: briefId });

                        if (config) {
                            const data = await unwrapAPIResult(api.request<IBrief>(config));
                            return data;
                        } else {
                            return null;
                        }
                    } catch {
                        return null; // Return null instead of throwing to allow other requests to continue
                    }
                }),
            );

            return allBriefs.filter((brief): brief is IBrief => brief !== null);
        } catch (error) {
            throw error;
        }
    }, [briefIds, api]);

    const queryResult = useQuery(queryKey, queryFn, {
        enabled: briefIds.length > 0,
        ...config,
    });

    return queryResult;
}
